<template>
  <section class="pp-card" :data-test="testId || null">
    <header v-if="title" class="pp-card__head">
      <span v-if="icon" class="pp-card__ic" aria-hidden="true"><i class="fa" :class="icon"></i></span>
      <h3 class="pp-card__title">{{ title }}</h3>
      <span v-if="count !== null && count !== undefined && count !== ''" class="pp-card__count">{{ count }}</span>
      <slot name="right"></slot>
    </header>
    <!--
      `table` cards get no padding and scroll sideways instead of squashing: a 6-column table on a
      phone is unreadable when every cell wraps to one word per line. The design does this with
      `:has(.tbl)`, which older iPads do not support — hence an explicit prop.
    -->
    <div class="pp-card__body" :class="{ 'pp-card__body--table': table }">
      <slot></slot>
    </div>
  </section>
</template>

<script>
/**
 * One card in the Patient Panel — the design's `Card` (patient-panel.jsx).
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
export default {
  name: 'PanelCard',

  props: {
    title: { type: String, default: '' },
    /** A Font Awesome 4 class, e.g. 'fa-calendar'. */
    icon: { type: String, default: '' },
    count: { type: [String, Number], default: null },
    table: { type: Boolean, default: false },
    testId: { type: String, default: '' },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.pp-card {
  background: $pp-surface;
  border: 1px solid $pp-line;
  border-radius: $pp-radius-lg;
  box-shadow: $pp-shadow;
  overflow: hidden;
  min-width: 0;
}

.pp-card__head {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 16px;
  border-bottom: 1px solid $pp-line-2;
  background: $pp-bg-2;
}

.pp-card__ic {
  width: 26px;
  height: 26px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: $pp-brand-soft;
  color: $pp-brand;
  font-size: 12px;
}

.pp-card__title {
  flex: 1 1 auto;
  margin: 0;
  font-size: 13.5px;
  font-weight: 700;
  color: $pp-ink;
}

.pp-card__count {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 99px;
  background: $pp-bg-3;
  color: $pp-ink-3;
  font-size: 11px;
  font-weight: 600;
}

.pp-card__body {
  padding: 14px 16px;
}

.pp-card__body--table {
  padding: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: $pp-phone-max) {
  .pp-card { border-radius: 14px; }
  .pp-card__head { padding: 11px 14px; }
  .pp-card__body { padding: 12px 14px; }
  .pp-card__body--table { padding: 0; }
}
</style>
